import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useChatStreamValue } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import { ChatMessageWithParts } from "src/views/assistant/types";
import UnifiedSidebar from "src/reusable/Sidebar/UnifiedSidebar";
import {
  usePartsAISidebar,
  PartsAISidebarContext,
} from "../../hooks/usePartsAISidebar";
import { useGetFeatureSheet } from "../../services/PartsAIFeatureSheetService";
import { useGetPartsAIRunData } from "../../services/PartsAIRunService";
import {
  LINKED_PARTS_QUERY_KEY,
  useLinkedParts,
  useSaveRunToPart,
} from "../../services/PartService";
import { useQueryClient } from "react-query";
import SidebarToggleButton from "src/reusable/Sidebar/SidebarToggleButton";
import SaveAsPartModal from "../SaveAsPartModal";
import { NucToast } from "src/reusable/components/Toast";

const EMPTY_ARTIFACTS = {};

interface PartsAISidebarProps {
  responseSources: any[];
  lastAssistantMessage?: ChatMessageWithParts | null;
  partsAIRunId: number | undefined;
}

/**
 * PartsAISidebar - Self-contained sidebar for Parts AI
 *
 * Wraps UnifiedSidebar with Parts AI-specific tabs:
 * - Sources (search results)
 * - Artifacts (tool outputs)
 * - Feature Sheet (when a run is active)
 * - Parts (linked parts for the current dialogue)
 */
export const PartsAISidebar = memo(
  ({ responseSources, partsAIRunId }: PartsAISidebarProps) => {
    const { isMobile } = useResponsive();

    // Get sidebar state from UIStore
    const { rightSidebar, setRightSidebar, preferences } = useUIStore();
    const showSideBar = rightSidebar.isOpen;
    // Honor the ``AutoOpenToggle`` shown in each tab header.
    const autoOpenEnabled = preferences?.autoOpenSidebar ?? true;

    // Local state for view mode
    const [viewMode, setViewMode] = useState<"dataChunk" | "cardList">(
      "dataChunk",
    );

    // Get dialogueId from route params
    const { dialogueId: dialogueIdParam } = useParams<{
      dialogueId: string;
    }>();
    const parsedDialogueId = Number(dialogueIdParam);
    const safeDialogueId = Number.isFinite(parsedDialogueId)
      ? parsedDialogueId
      : undefined;

    // Read artifacts from the host's stream (provided by PartsAIChat).
    const artifacts = useChatStreamValue((s) => s.artifacts, EMPTY_ARTIFACTS);

    // Compute stable artifacts length for dependency tracking
    const artifactsLength = Object.keys(artifacts).length;

    // Linked parts (Parts tab). Refetches are event-driven by the artifact watcher.
    const { data: linkedPartsData } = useLinkedParts(safeDialogueId);
    const linkedPartCount = linkedPartsData?.length ?? 0;
    const queryClient = useQueryClient();

    // Feature sheet data (shares React Query cache with PartsAIFeatureSheet)
    const { data: featureSheetData, refetch: refetchFeatureSheet } =
      useGetFeatureSheet(partsAIRunId);
    const featureSheetRowCount = featureSheetData?.features
      ? Object.keys(featureSheetData.features).length
      : 0;

    // Run metadata
    const { data: runData } = useGetPartsAIRunData(partsAIRunId);
    const isRunDataLoaded = partsAIRunId === undefined || runData !== undefined;
    const isEditingExistingPart = runData?.editing_part_fk != null;
    const saveRunToPart = useSaveRunToPart();

    // Watch for completed "feature sheet" tool artifacts and refetch + auto-open tab
    // This runs here (always mounted) rather than in PartsAIFeatureSheet (only mounted when tab is active)
    const completedFeatureSheetCount = useMemo(
      () =>
        Object.values(artifacts).filter(
          (a: any) =>
            a.toolName
              ?.toLowerCase()
              .replace(/ /g, "_")
              .includes("feature_sheet") &&
            a.content?.text &&
            !a.isLoading,
        ).length,
      [artifacts],
    );
    const processedCountRef = useRef(0);

    // Reset processed count when navigating between chats
    useEffect(() => {
      processedCountRef.current = 0;
    }, [safeDialogueId, partsAIRunId]);

    useEffect(() => {
      if (
        completedFeatureSheetCount > 0 &&
        completedFeatureSheetCount > processedCountRef.current
      ) {
        processedCountRef.current = completedFeatureSheetCount;
        refetchFeatureSheet();
        // Only auto-select a tab when the sidebar is currently closed
        // and we're opening it.
        if (autoOpenEnabled && !showSideBar) {
          setRightSidebar({
            isOpen: true,
            selectedItem: "featureSheet",
            isArtifactItemOpen: false,
            currentArtifactId: null,
          });
        }
      }
    }, [
      completedFeatureSheetCount,
      refetchFeatureSheet,
      setRightSidebar,
      autoOpenEnabled,
      showSideBar,
    ]);

    // Search-tool watcher.
    const completedSearchToolCount = useMemo(
      () =>
        Object.values(artifacts).filter((a: any) => {
          const tn = a.toolName
            ?.toLowerCase()
            .replace(/ /g, "_")
            .replace(/-/g, "_");
          if (!tn) return false;
          const isSearchTool =
            tn.includes("search_parts") || tn.includes("clear_linked_parts");
          return isSearchTool && a.content?.text && !a.isLoading;
        }).length,
      [artifacts],
    );
    const processedSearchToolCountRef = useRef(0);

    // Reset processed count when navigating between chats.
    useEffect(() => {
      processedSearchToolCountRef.current = 0;
    }, [safeDialogueId, partsAIRunId]);

    useEffect(() => {
      if (
        completedSearchToolCount > 0 &&
        completedSearchToolCount > processedSearchToolCountRef.current
      ) {
        processedSearchToolCountRef.current = completedSearchToolCount;
        if (safeDialogueId !== undefined) {
          queryClient.invalidateQueries([
            LINKED_PARTS_QUERY_KEY,
            safeDialogueId,
          ]);
        }
        // Only auto-select a tab when the sidebar is currently closed
        // and we're opening it.
        if (autoOpenEnabled && !showSideBar) {
          setRightSidebar({
            isOpen: true,
            selectedItem: "parts",
            isArtifactItemOpen: false,
            currentArtifactId: null,
          });
        }
      }
    }, [
      completedSearchToolCount,
      safeDialogueId,
      queryClient,
      setRightSidebar,
      autoOpenEnabled,
      showSideBar,
    ]);

    // Memoize total sidebar items for mobile badge.
    const totalSidebarItems = useMemo(() => {
      return responseSources.length + artifactsLength + linkedPartCount;
    }, [responseSources.length, artifactsLength, linkedPartCount]);

    // Memoize context to prevent unnecessary re-renders of UnifiedSidebar
    const featureSheetFeatures = featureSheetData?.features ?? null;

    // Save as Part modal state
    const [saveAsPartOpen, setSaveAsPartOpen] = useState(false);

    const handleSaveChangesToPart = useCallback(() => {
      if (!partsAIRunId) return;
      saveRunToPart.mutate(partsAIRunId, {
        onSuccess: () => {
          NucToast({
            message: "Saved changes to part.",
            type: "success",
            hideProgressBar: false,
          });
        },
        onError: () => {
          NucToast({
            message: "Failed to save changes to part. Please try again.",
            type: "error",
            hideProgressBar: false,
          });
        },
      });
    }, [partsAIRunId, saveRunToPart]);

    const handleOpenSaveAsPartModal = useCallback(() => {
      setSaveAsPartOpen(true);
    }, []);

    const sidebarContext: PartsAISidebarContext = useMemo(
      () => ({
        responseSources,
        artifacts,
        viewMode,
        setViewMode,
        partsAIRunId,
        featureSheetRowCount,
        featureSheetFeatures,
        isRunDataLoaded,
        isEditingExistingPart,
        isSavingChanges: saveRunToPart.isLoading,
        onSaveChangesToPart: handleSaveChangesToPart,
        onOpenSaveAsPartModal: handleOpenSaveAsPartModal,
        dialogueId: safeDialogueId,
        linkedPartCount,
      }),
      [
        responseSources,
        artifacts,
        viewMode,
        partsAIRunId,
        featureSheetRowCount,
        featureSheetFeatures,
        isRunDataLoaded,
        isEditingExistingPart,
        saveRunToPart.isLoading,
        handleSaveChangesToPart,
        handleOpenSaveAsPartModal,
        safeDialogueId,
        linkedPartCount,
      ],
    );

    // Get sidebar configuration
    const sidebarConfig = usePartsAISidebar(sidebarContext);

    // Mobile badge content
    const handleMobileToggle = useCallback(() => {
      setRightSidebar({
        isOpen: !showSideBar,
        ...(!showSideBar &&
          rightSidebar.selectedItem === null && { selectedItem: "sources" }),
      });
    }, [showSideBar, rightSidebar.selectedItem, setRightSidebar]);

    if (safeDialogueId === undefined) {
      return null;
    }

    return (
      <>
        {/* The "Save as Part" button. */}
        {partsAIRunId && !isEditingExistingPart && (
          <SaveAsPartModal
            open={saveAsPartOpen}
            onClose={() => setSaveAsPartOpen(false)}
            runId={partsAIRunId}
          />
        )}

        <UnifiedSidebar
          config={sidebarConfig}
          context={sidebarContext}
          open={rightSidebar.isOpen}
          onOpenChange={(open) => setRightSidebar({ isOpen: open })}
          selectedTab={rightSidebar.selectedItem}
          onTabChange={(tab) =>
            setRightSidebar({
              selectedItem: tab,
              isArtifactItemOpen: false,
              currentArtifactId: null,
            })
          }
          width={rightSidebar.width}
          onWidthChange={(width) => setRightSidebar({ width })}
        />

        {isMobile && (
          <SidebarToggleButton
            open={showSideBar}
            onClick={handleMobileToggle}
            badgeContent={totalSidebarItems}
            tooltipTitle={
              showSideBar ? "Close sidebar" : `View ${totalSidebarItems} items`
            }
            show={totalSidebarItems > 0}
          />
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.responseSources === nextProps.responseSources &&
      prevProps.lastAssistantMessage?.id ===
        nextProps.lastAssistantMessage?.id &&
      prevProps.partsAIRunId === nextProps.partsAIRunId
    );
  },
);

PartsAISidebar.displayName = "PartsAISidebar";

export default PartsAISidebar;
